import { Liff } from "@line/liff/exports";
import { Context, createContext, useCallback, useMemo, useState } from "react";
import * as ReactQuery from "react-query";
import * as GraphQLClient from "@apollo/client";
import Header from "./header";
import styled from "styled-components";
import { GetOneAheadUserInfo, GetOrRegisterUser } from "@/graphql/documentNode";
import { OneAheadUser, User } from "@/graphql/types";
import { Device, classifyDevice } from "@/lib/ts/device";
import { useSnackBar } from "@/lib/ts/hooks/useSnackBar";

type Props = {
  children: JSX.Element[] | JSX.Element;
};

export type LiffContextType = {
  user: User | undefined;
  loading: boolean | any;
  device: Device | undefined;
};
export const LiffContext: Context<LiffContextType> = createContext({
  user: undefined as any,
  loading: false,
  device: undefined as any,
});

/**
 * 子コンポーネントでliffオブジェクトを共用可能にする
 * @param props
 * @returns
 */
const LiffProvider = (props: Props) => {
  const [liff, setLiff] = useState(undefined as Liff | undefined);

  const [lineIdToken, setLineIdToken] = useState(null as string | null);

  const [user, setUser] = useState(undefined as User | undefined);

  const [loading, setLoading] = useState(true);

  const [loggedIn, setLoggedIn] = useState(false);

  const liffId = process.env.REACT_APP_LIFF_ID!;

  const snackBar1 = useSnackBar({ loadingMessage: "この処理には15秒ほどかかる場合があります" })
  const snackBar2 = useSnackBar({ loadingMessage: "この処理には15秒ほどかかる場合があります" })


  const [getOrRegisterUser] = GraphQLClient.useMutation(GetOrRegisterUser, {
    onCompleted: (data) => {
      const oneAheadUser: OneAheadUser = data.getOrRegisterUser
      const user: User = {
        id: oneAheadUser.id,
        lineId: oneAheadUser.line_id || "",
        lineName: oneAheadUser.line_name || "",
        profilePictureUrl: oneAheadUser.line_picture || "",
        admin: oneAheadUser.admin || false,
        owner: oneAheadUser.owner || false,
        point: 0,
      }
      setUser(user)
      snackBar2.onQuerySuccess()
    },
    onError: (err) => {
      console.log(err)
    },
    context: {
      clientName: "oneAhead",
    }
  })


  const getUserInfo = useCallback(async (token: string) => {
    const params = new URLSearchParams();
    params.append("id_token", token);
    params.append("client_id", process.env.REACT_APP_LIFF_CLIENT_ID!);
    const res = await fetch(process.env.REACT_APP_OAUTH_URL!, {
      method: "POST",
      body: params,
    });
    const json = await res.json();
    return json;
  }, []);

  ReactQuery.useQuery([loggedIn], async () => {
    let loggedInTemp = loggedIn

    snackBar1.loadStart()
    const liffObj =
      typeof window !== undefined
        ? (await import("@line/liff")).liff
        : undefined;
    
    if (!liffObj) {
      snackBar1.onQueryError()
      return;
    }

    const liffState = new URLSearchParams(window.location.search).get("liff.state")
    console.log({liffState})
    const redirectURL = new URLSearchParams(window.location.search).get("redirect")
    if(redirectURL) {
      const decodedURL = decodeURIComponent(redirectURL)
      console.log({decodedURL})
      localStorage.setItem("1AHEAD_RESERVATION_REDIRECT_URL", decodedURL)
    }
    if(!loggedIn) {
      console.log("liff init start")
      snackBar1.loadStart()
      await liffObj.init({
        liffId,
        withLoginOnExternalBrowser: true,
      });
      console.log("liff init end")
      snackBar1.onQuerySuccess()
      if (!loggedIn) {
        loggedInTemp = liffObj.isLoggedIn()
      }

      const token = liffObj.getIDToken();
      setLineIdToken(token);
      console.log("init", token);
  
      
      console.log("getUserInfo start")
      snackBar1.loadStart()
      const result = token ? await getUserInfo(token) : undefined;
      console.log("getUserInfo end")
      snackBar1.onQuerySuccess()
      if (!result || result.error_description === "IdToken expired.") {
        liffObj.logout();
        if (loggedIn) {
          loggedInTemp = false
        }
        
        snackBar1.loadStart()
        console.log("liff init restart")
        await liffObj.init({
          liffId,
          withLoginOnExternalBrowser: true,
        });
        console.log("liff init end")
        snackBar1.onQuerySuccess()
  
        if (!loggedIn) {
          loggedInTemp = liffObj.isLoggedIn()
        }
        const newToken = liffObj.getIDToken();
        setLineIdToken(newToken);
        console.log("newToken", newToken);
  
        if (newToken) {
          snackBar1.loadStart()
          console.log("getUserInfo restart")
          await getUserInfo(newToken);
          console.log("getUserInfo end")
          snackBar1.onQuerySuccess()
          setLiff(liffObj);
          setLoggedIn(loggedInTemp)
          return newToken;
        } else {
          setLoggedIn(loggedInTemp)
          return undefined;
        }
      } else if (result && !result.error_description) {
        setLiff(liffObj);
        setLoggedIn(loggedInTemp)
        return token;
      }
    }
    /*
    const restoredRedirectURL = localStorage.getItem("1AHEAD_RESERVATION_REDIRECT_URL")
    console.log({restoredRedirectURL})
    if(loggedInTemp && restoredRedirectURL) {
      localStorage.removeItem("1AHEAD_RESERVATION_REDIRECT_URL")
      window.location.href = restoredRedirectURL 
    }
    */
    snackBar1.onQuerySuccess()
  });

  const [getOneAheadUser] = GraphQLClient.useLazyQuery(GetOneAheadUserInfo, {
    variables: {
      idToken: lineIdToken,
    },
    onCompleted: (data) => {
      console.log("user", data);
      setUser(data.getOneAheadUserInfo as User);
      snackBar2.onQuerySuccess()
    },
    onError: (err) => {
      console.log(err)
    }
  });

  ReactQuery.useQuery(
    [lineIdToken],
    async () => {
      snackBar2.loadStart()
      console.log("getOneAheadUser start")
      await getOrRegisterUser({
        variables: {
          input : {
            idToken: lineIdToken,
            clientId: process.env.REACT_APP_LIFF_CLIENT_ID,
          }
        }
      });
      console.log("getOneAheadUser end")
    },
  );

  const liffContext: LiffContextType = useMemo(
    () => ({
      user,
      loading,
      device: classifyDevice(),
    }),
    [loading, user],
  );

  return (
    <LiffContext.Provider value={liffContext}>
      <Header />
      <LiffProviderStyle>{user ? props.children : <></>}</LiffProviderStyle>
      {snackBar1.component}
      {snackBar2.component}
    </LiffContext.Provider>
  );
};

export const LiffProviderStyle = styled.div`
  padding-top: 70px;
`;

export default LiffProvider;
